import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { RefreshCw, Lock, Unlock, CheckSquare, Square } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Modal } from '@/shared/components/ui/Modal';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useAttendanceRange, useAttendanceActions } from '../hooks/useBiometricAdmin';
import {
  STATUS_CELL,
  STATUS_LABELS,
  minutesToHhMm,
  istTime,
  type AttendanceDay,
  type AttendanceStatus,
} from '../biometricTypes';

const currentMonth = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7);

/** Month × employee grid of status codes; click a cell to override a day. */
export function MusterRollGrid() {
  const [month, setMonth] = useState(currentMonth);
  const [editing, setEditing] = useState<AttendanceDay | null>(null);
  const [form, setForm] = useState({ status: '', reason: '' });

  // Bulk-select mode — a whole run of days often needs the same correction
  // at once (e.g. the device was offline for a week), and one-cell-at-a-time
  // overrides made that tedious. Toggling this mode repurposes cell clicks
  // from "open the single-edit modal" to "add/remove from the selection".
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedCells, setSelectedCells] = useState<Map<string, AttendanceDay>>(new Map());
  const [bulkForm, setBulkForm] = useState({ status: '', reason: '' });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const lastDay = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m, 0).getDate();
  }, [month]);

  const from = `${month}-01`;
  const to = `${month}-${String(lastDay).padStart(2, '0')}`;

  const { data: rows, isLoading } = useAttendanceRange(from, to);
  const { recompute, override, lock, unlock } = useAttendanceActions();

  const grid = useMemo(() => {
    const byUser = new Map<string, { name: string; dept: string; days: Record<string, AttendanceDay> }>();
    for (const row of rows ?? []) {
      const id = row.user_id?._id ?? row.biometric_id ?? 'unknown';
      if (!byUser.has(id)) {
        byUser.set(id, {
          name: row.user_id?.display_name ?? `PIN ${row.biometric_id}`,
          dept: row.user_id?.department ?? '',
          days: {},
        });
      }
      byUser.get(id)!.days[row.log_date] = row;
    }
    return [...byUser.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const dayNumbers = Array.from({ length: lastDay }, (_, i) => i + 1);

  const act = async (fn: Promise<unknown>, message: string) => {
    try {
      await fn;
      toast.success(message);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  /** Each cell is overridden individually so one failure doesn't abort the batch. */
  const saveBulkOverride = async () => {
    if (!bulkForm.reason.trim()) {
      toast.error('A reason is required — overrides are audited.');
      return;
    }
    setBulkSaving(true);
    let ok = 0;
    const failures: string[] = [];
    for (const cell of selectedCells.values()) {
      try {
        await override.mutateAsync({ id: cell._id, status: bulkForm.status, reason: bulkForm.reason.trim() });
        ok += 1;
      } catch (err) {
        failures.push(getApiErrorMessage(err));
      }
    }
    setBulkSaving(false);
    setSelectedCells(new Map());
    setBulkOpen(false);
    if (ok) toast.success(`Overrode ${ok} day${ok === 1 ? '' : 's'}.`);
    if (failures.length) toast.error(`${failures.length} could not be overridden. ${failures[0]}`);
  };

  const saveOverride = async () => {
    if (!editing) return;
    if (!form.reason.trim()) {
      toast.error('A reason is required — overrides are audited.');
      return;
    }
    try {
      await override.mutateAsync({
        id: editing._id,
        status: form.status || editing.status,
        reason: form.reason.trim(),
      });
      toast.success('Day overridden.');
      setEditing(null);
      setForm({ status: '', reason: '' });
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <Card className="space-y-4 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-on-surface">Muster roll</h2>
          <p className="text-sm text-on-surface-variant">
            Click any cell to override that day. Locking a month freezes it against recomputation.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="month" className="w-40" value={month} onChange={e => setMonth(e.target.value)} />
          <Button
            variant="secondary"
            onClick={() => act(recompute.mutateAsync({ from, to }), 'Recompute finished.')}
            disabled={recompute.isPending}
          >
            <RefreshCw className={`mr-1 h-4 w-4 ${recompute.isPending ? 'animate-spin' : ''}`} />
            Recompute
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              if (!window.confirm(`Lock ${month} for payroll? Recomputation will skip these rows.`)) return;
              act(lock.mutateAsync(month), `${month} locked.`);
            }}
          >
            <Lock className="mr-1 h-4 w-4" />
            Lock
          </Button>
          <Button variant="secondary" onClick={() => act(unlock.mutateAsync(month), `${month} unlocked.`)}>
            <Unlock className="mr-1 h-4 w-4" />
            Unlock
          </Button>
          <Button
            variant={bulkMode ? 'primary' : 'secondary'}
            onClick={() => {
              setBulkMode((v) => !v);
              setSelectedCells(new Map());
            }}
          >
            {bulkMode ? <CheckSquare className="mr-1 h-4 w-4" /> : <Square className="mr-1 h-4 w-4" />}
            Bulk select
          </Button>
        </div>
      </div>

      {bulkMode && selectedCells.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-outline bg-surface-container-low px-3 py-2">
          <span className="text-sm text-on-surface">{selectedCells.size} day{selectedCells.size === 1 ? '' : 's'} selected</span>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => { setBulkForm({ status: 'P', reason: '' }); setBulkOpen(true); }}>
              Bulk override
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedCells(new Map())}>Clear</Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 text-xs">
        {(Object.keys(STATUS_LABELS) as AttendanceStatus[]).map(code => (
          <span key={code} className={`rounded px-1.5 py-0.5 ${STATUS_CELL[code]}`}>
            {code} — {STATUS_LABELS[code]}
          </span>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center p-12"><Spinner /></div>
      ) : grid.length === 0 ? (
        <p className="py-8 text-center text-sm text-on-surface-variant">
          Nothing computed for {month} yet. Press Recompute once punches have arrived.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-surface px-2 py-1 text-left font-medium text-on-surface-variant">
                  Employee
                </th>
                {dayNumbers.map(n => (
                  <th key={n} className="w-7 px-0.5 py-1 text-center font-medium text-on-surface-variant">
                    {n}
                  </th>
                ))}
                <th className="px-2 py-1 text-center font-medium text-on-surface-variant">P</th>
                <th className="px-2 py-1 text-center font-medium text-on-surface-variant">A</th>
                <th className="px-2 py-1 text-center font-medium text-on-surface-variant">OT</th>
              </tr>
            </thead>
            <tbody>
              {grid.map(emp => {
                const cells = dayNumbers.map(n => emp.days[`${month}-${String(n).padStart(2, '0')}`]);
                const present = cells.filter(c => c?.status === 'P' || c?.status === 'HD').length;
                const absent = cells.filter(c => c?.status === 'A' || c?.status === 'MP').length;
                const ot = cells.reduce((sum, c) => sum + (c?.ot_minutes ?? 0), 0);
                return (
                  <tr key={emp.name} className="border-t border-outline-variant/40">
                    <td className="sticky left-0 z-10 max-w-[180px] truncate bg-surface px-2 py-1 font-medium text-on-surface">
                      {emp.name}
                    </td>
                    {cells.map((cell, i) => (
                      <td key={i} className="p-0.5 text-center">
                        {cell ? (
                          <button
                            type="button"
                            title={
                              bulkMode
                                ? `${cell.log_date} · click to ${selectedCells.has(cell._id) ? 'deselect' : 'select'}`
                                : `${cell.log_date} · ${STATUS_LABELS[cell.status]}\nIn ${istTime(cell.first_in)} Out ${istTime(cell.last_out)}\nWorked ${minutesToHhMm(cell.worked_minutes)}${cell.locked ? '\nLocked' : ''}`
                            }
                            onClick={() => {
                              if (bulkMode) {
                                if (cell.locked) return;
                                setSelectedCells((prev) => {
                                  const next = new Map(prev);
                                  if (next.has(cell._id)) next.delete(cell._id);
                                  else next.set(cell._id, cell);
                                  return next;
                                });
                                return;
                              }
                              setEditing(cell);
                              setForm({ status: cell.status, reason: '' });
                            }}
                            className={`h-6 w-6 rounded text-[10px] font-semibold ${STATUS_CELL[cell.status]} ${
                              cell.is_manual_override ? 'ring-1 ring-primary' : ''
                            } ${bulkMode && selectedCells.has(cell._id) ? 'ring-2 ring-offset-1 ring-primary' : ''} ${
                              bulkMode && cell.locked ? 'opacity-40' : ''
                            }`}
                          >
                            {cell.status}
                          </button>
                        ) : (
                          <span className="text-on-surface-variant">·</span>
                        )}
                      </td>
                    ))}
                    <td className="px-2 py-1 text-center font-semibold text-success-text">{present}</td>
                    <td className="px-2 py-1 text-center font-semibold text-danger-text">{absent}</td>
                    <td className="px-2 py-1 text-center text-on-surface-variant">{(ot / 60).toFixed(1)}h</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing ? `Override ${editing.log_date}` : ''}
      >
        {editing && (
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-1 text-sm">
              <dt className="text-on-surface-variant">Computed status</dt>
              <dd className="text-on-surface">{STATUS_LABELS[editing.status]}</dd>
              <dt className="text-on-surface-variant">First in / last out</dt>
              <dd className="text-on-surface">{istTime(editing.first_in)} — {istTime(editing.last_out)}</dd>
              <dt className="text-on-surface-variant">Worked / OT</dt>
              <dd className="text-on-surface">
                {minutesToHhMm(editing.worked_minutes)} / {minutesToHhMm(editing.ot_minutes)}
              </dd>
              <dt className="text-on-surface-variant">Late / early out</dt>
              <dd className="text-on-surface">
                {minutesToHhMm(editing.late_minutes)} / {minutesToHhMm(editing.early_out_minutes)}
              </dd>
            </dl>

            {editing.is_manual_override && (
              <Badge tone="primary">Already overridden: {editing.override_reason}</Badge>
            )}
            {editing.locked && (
              <p className="rounded bg-warning/10 p-2 text-sm text-warning-text">
                This month is locked for payroll. Unlock it before editing.
              </p>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-on-surface">Status</label>
              <select
                className="h-10 w-full rounded-md border border-outline-variant bg-surface px-3 text-sm text-on-surface"
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              >
                {(Object.keys(STATUS_LABELS) as AttendanceStatus[]).map(code => (
                  <option key={code} value={code}>{code} — {STATUS_LABELS[code]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-on-surface">Reason (required)</label>
              <Input
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. device offline, verified with manager"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveOverride} disabled={editing.locked || override.isPending}>
                Save override
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title={`Bulk override ${selectedCells.size} day${selectedCells.size === 1 ? '' : 's'}`}>
        <div className="space-y-3">
          <p className="text-sm text-on-surface-variant">
            Applies the same status and reason to every selected day. Each one is still recorded as its own audited override.
          </p>
          <div>
            <label className="mb-1 block text-sm font-medium text-on-surface">Status</label>
            <select
              className="h-10 w-full rounded-md border border-outline-variant bg-surface px-3 text-sm text-on-surface"
              value={bulkForm.status}
              onChange={e => setBulkForm(f => ({ ...f, status: e.target.value }))}
            >
              {(Object.keys(STATUS_LABELS) as AttendanceStatus[]).map(code => (
                <option key={code} value={code}>{code} — {STATUS_LABELS[code]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-on-surface">Reason (required)</label>
            <Input
              value={bulkForm.reason}
              onChange={e => setBulkForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="e.g. device offline all week, verified with manager"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={saveBulkOverride} loading={bulkSaving}>
              Save {selectedCells.size} override{selectedCells.size === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
